/* eslint-disable react-hooks/exhaustive-deps */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { enqueueSnackbar } from 'notistack';
import { Company, CustomPrimaryUser, UserPermission } from 'api';
import { COMPANY, USE_PORTAL } from 'common/constants/user';
import { useGetFirmUsersSlim } from 'services/hooks/useGetFirmUsers';
import { getUserPermissions } from 'services/hooks/useUserSelected';
import { ExtendedCompanyDataWithPermissions, SlimUser } from './types';

const useCompanyCategoriesProcessManagement = ({ firmId, companies }: { firmId: number; companies: Company[] }) => {
  const HAS_MEASUREMENT_DATE = true;
  const NO_MEASUREMENT_DATE = false;
  const HAS_USERS = true;
  const NO_USERS = false;

  const { getFirmUsersSlim } = useGetFirmUsersSlim(firmId);
  const [users, setUsers] = useState<SlimUser[]>([]);
  const [userPermissions, setUserPermissions] = useState<UserPermission[]>([]);
  const [companyCategories, setCompanyCategories] = useState<{
    companiesWithMeasurementDateAndUsers: ExtendedCompanyDataWithPermissions[];
    companiesWithMeasurementDateButNoUsers: ExtendedCompanyDataWithPermissions[];
    companiesWithoutMeasurementDateButUsers: ExtendedCompanyDataWithPermissions[];
    companiesWithoutMeasurementDateAndUsers: ExtendedCompanyDataWithPermissions[];
    companiesWithRequests: ExtendedCompanyDataWithPermissions[];
  }>({
    companiesWithMeasurementDateAndUsers: [],
    companiesWithMeasurementDateButNoUsers: [],
    companiesWithoutMeasurementDateButUsers: [],
    companiesWithoutMeasurementDateAndUsers: [],
    companiesWithRequests: [],
  });

  // functions to get and set firm users and permissions
  const handleGetAndSetFirmUsers = useCallback(async (): Promise<void> => {
    try {
      const tmpUsers = (await getFirmUsersSlim()) as SlimUser[];
      setUsers(tmpUsers);
    } catch (error: any) {
      enqueueSnackbar(error.message, { variant: 'error' });
    }
  }, [getFirmUsersSlim]);

  const handleGetAndSetFirmUsersPermissions = useCallback(async (): Promise<void> => {
    try {
      const promises = users.map(user => {
        if (user.id !== undefined) {
          return getUserPermissions(user.id, firmId);
        }
        return Promise.resolve([]);
      });
      const tmpUserPermissions = await Promise.all(promises);
      setUserPermissions(tmpUserPermissions.flat());
    } catch (error: any) {
      enqueueSnackbar(error.message, { variant: 'error' });
    }
  }, [users, firmId]);

  // function to get user permissions for a company
  const getUserPermissionsForCompany = (userPermissionsParam: UserPermission[], company: Company): UserPermission[] =>
    userPermissionsParam.filter((permission: UserPermission) => {
      if (!permission?.feature_object) {
        return false;
      }

      const {
        feature_object: { object_type: objectType, object_id: objectId },
        access_type: accessType,
      } = permission;

      const isCompanyType = objectType === COMPANY;
      const isAccessType = accessType === USE_PORTAL;
      const isCompanyId = objectId === company.id;

      return isCompanyType && isAccessType && isCompanyId;
    });

  const handleGetUserPermissions = (company: Company): UserPermission[] | null => {
    const userPermissionsForCompany = getUserPermissionsForCompany(userPermissions, company);
    if (userPermissionsForCompany.length) {
      return userPermissionsForCompany;
    }
    return null;
  };

  const mapUserPermissionsToUsers = (
    permission: UserPermission,
    usersParam: CustomPrimaryUser[]
  ): { id: number; email: string } | undefined => {
    const user = usersParam.find(userParam => userParam.id === permission.user_id);
    if (user && user.id !== undefined) {
      return {
        id: user.id,
        email: user.email || '',
      };
    }
    return undefined;
  };

  const processCompany = (company: Company, usersParam: CustomPrimaryUser[]): ExtendedCompanyDataWithPermissions => {
    const tmpUsers = handleGetUserPermissions(company);
    const usersWithCompanyPermissions = tmpUsers
      ?.map(permission => mapUserPermissionsToUsers(permission, usersParam))
      .filter(Boolean) as { id: number; email: string }[];

    const baseCompany: ExtendedCompanyDataWithPermissions = {
      ...company,
      requested_documents: (company as any).requested_documents || [],
      questions: (company as any).questions || [],
      has_tasks: (company as any).has_tasks,
      latest_cmd: (company as any).latest_cmd,
    };

    if (tmpUsers && tmpUsers.length > 0) {
      return { ...baseCompany, users_with_company_permissions: usersWithCompanyPermissions };
    }
    return baseCompany;
  };

  // to get and set company categories first I need to get the companies with and without users
  const getCompaniesWithAndWithoutUsers = (
    companiesParam: Company[],
    userPermissionsParam: UserPermission[],
    usersParam: CustomPrimaryUser[]
  ) => {
    if (!userPermissionsParam.length) {
      return { companiesWithUsers: [], companiesWithoutUsers: [], companiesWithRequests: [] };
    }

    const companiesWithUsers: ExtendedCompanyDataWithPermissions[] = [];
    const companiesWithoutUsers: ExtendedCompanyDataWithPermissions[] = [];
    const companiesWithRequests: ExtendedCompanyDataWithPermissions[] = [];
    companiesParam.forEach(company => {
      const processedCompany = processCompany(company, usersParam);
      if (processedCompany.has_tasks) {
        companiesWithRequests.push(processedCompany);
      } else if (processedCompany.users_with_company_permissions) {
        companiesWithUsers.push(processedCompany);
      } else {
        companiesWithoutUsers.push(processedCompany);
      }
    });

    return { companiesWithUsers, companiesWithoutUsers, companiesWithRequests };
  };

  const [companiesWithUsers, companiesWithoutUsers, companiesWithRequests] = useMemo(() => {
    if (userPermissions.length && users.length) {
      const {
        companiesWithUsers: tmpCompaniesWithUsers,
        companiesWithoutUsers: tmpCompaniesWithoutUsers,
        companiesWithRequests: tmpCompaniesWithRequests,
      } = getCompaniesWithAndWithoutUsers(companies, userPermissions, users);
      return [tmpCompaniesWithUsers, tmpCompaniesWithoutUsers, tmpCompaniesWithRequests];
    }
    return [[], [], []];
  }, [companies, userPermissions, users]);

  const companyHasUsersWithPermissions = (company: ExtendedCompanyDataWithPermissions): boolean =>
    Boolean(company.users_with_company_permissions);
  const companyHasMeasurementDate = (company: ExtendedCompanyDataWithPermissions): boolean =>
    Boolean(company.latest_cmd);

  const filterCompanies = (
    companiesParam: ExtendedCompanyDataWithPermissions[],
    hasMeasurementDateCondition: boolean,
    hasUsersCondition: boolean
  ): ExtendedCompanyDataWithPermissions[] =>
    companiesParam.filter(
      company =>
        companyHasMeasurementDate(company) === hasMeasurementDateCondition &&
        companyHasUsersWithPermissions(company) === hasUsersCondition
    );

  // functions set company categories
  const handleSetCompaniesCategories = useCallback((): void => {
    const companiesWithMeasurementDateAndUsers = filterCompanies(companiesWithUsers, HAS_MEASUREMENT_DATE, HAS_USERS);
    const companiesWithMeasurementDateButNoUsers = filterCompanies(
      companiesWithoutUsers,
      HAS_MEASUREMENT_DATE,
      NO_USERS
    );
    const companiesWithoutMeasurementDateButUsers = filterCompanies(companiesWithUsers, NO_MEASUREMENT_DATE, HAS_USERS);
    const companiesWithoutMeasurementDateAndUsers = filterCompanies(
      companiesWithoutUsers,
      NO_MEASUREMENT_DATE,
      NO_USERS
    );

    setCompanyCategories({
      companiesWithMeasurementDateAndUsers,
      companiesWithMeasurementDateButNoUsers,
      companiesWithoutMeasurementDateButUsers,
      companiesWithoutMeasurementDateAndUsers,
      companiesWithRequests,
    });
  }, [companiesWithoutUsers, companiesWithUsers, companiesWithRequests]);

  useEffect(() => {
    if (firmId) {
      handleGetAndSetFirmUsers();
    }
  }, [firmId]);

  useEffect(() => {
    if (users.length && firmId) {
      handleGetAndSetFirmUsersPermissions();
    }
  }, [users, firmId]);

  useEffect(() => {
    if (companiesWithUsers.length || companiesWithoutUsers.length || companiesWithRequests.length) {
      handleSetCompaniesCategories();
    }
  }, [companiesWithUsers, companiesWithoutUsers, companiesWithRequests]);

  return { companyCategories };
};

export default useCompanyCategoriesProcessManagement;
